// UGOT Pro 관리 화면의 표시·판정 규칙(순수 함수). 화면 컴포넌트와 시험이 같이 쓴다.
import type {
  UgotProAdminOrder,
  UgotProAdminSubscription,
  UgotProGrantState,
  UgotProRefundRequest,
  UgotProRefundStatus,
} from './ugotProTypes';

export const UGOT_PRO_DEFAULT_GRANT_NOTE = '가족';
export const UGOT_PRO_DEFAULT_GRANT_DAYS = 365;
export const UGOT_PRO_MAX_GRANT_DAYS = 1830;
const DAY_MS = 24 * 60 * 60 * 1000;

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;

/** pay 는 UTC 를 오프셋 없이 보낸다. 오프셋이 없으면 UTC 로 읽는다(브라우저 현지 시각으로 잘못 읽지 않게). */
export const parseUgotProUtc = (value: string | null | undefined): Date | null => {
  if (!value || !value.trim()) return null;
  const trimmed = value.trim();
  const parsed = new Date(HAS_OFFSET.test(trimmed) ? trimmed : `${trimmed}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const pad = (value: number) => String(value).padStart(2, '0');

const localDate = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** 관리자 브라우저 현지 시각으로 "YYYY-MM-DD HH:mm". 값이 없으면 '-'. */
export const formatUgotProDateTime = (value: string | null | undefined): string => {
  const date = parseUgotProUtc(value);
  if (!date) return value?.trim() ? value : '-';
  return `${localDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const formatUgotProDate = (value: string | null | undefined): string => {
  const date = parseUgotProUtc(value);
  return date ? localDate(date) : '-';
};

/** 날짜 입력칸 기본값: 오늘부터 365일 뒤(현지 날짜). */
export const defaultUgotProGrantDate = (now: Date = new Date()): string => (
  localDate(new Date(now.getTime() + UGOT_PRO_DEFAULT_GRANT_DAYS * DAY_MS))
);

/** 이미 권한이 있으면 그 만료일에서 1년 연장한 날을 기본값으로 준다(만료일이 지났으면 오늘부터). */
export const suggestUgotProGrantDate = (currentExpiresAt: string | null, now: Date = new Date()): string => {
  const current = parseUgotProUtc(currentExpiresAt);
  const base = current && current.getTime() > now.getTime() ? current : now;
  const suggested = new Date(base.getTime() + UGOT_PRO_DEFAULT_GRANT_DAYS * DAY_MS);
  const limit = new Date(now.getTime() + UGOT_PRO_MAX_GRANT_DAYS * DAY_MS);
  return localDate(suggested.getTime() > limit.getTime() ? limit : suggested);
};

/** 날짜 입력값(YYYY-MM-DD)을 그날 현지 23:59:59 의 UTC ISO 로. 미래·최대 1830일 안인지 확인한다. */
export const ugotProDateInputToExpiresAt = (value: string, now: Date = new Date()): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) throw new Error('만료일을 YYYY-MM-DD 로 골라 주세요.');
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59);
  if (Number.isNaN(date.getTime()) || date.getTime() <= now.getTime()) {
    throw new Error('만료일은 오늘 이후여야 합니다.');
  }
  if (date.getTime() - now.getTime() > UGOT_PRO_MAX_GRANT_DAYS * DAY_MS) {
    throw new Error(`만료일은 오늘부터 최대 ${UGOT_PRO_MAX_GRANT_DAYS}일 뒤까지 정할 수 있습니다.`);
  }
  return date.toISOString();
};

export type BadgeTone = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';

export const UGOT_PRO_GRANT_STATE_LABELS: Record<UgotProGrantState, { label: string; tone: BadgeTone }> = {
  ACTIVE: { label: '이용 중', tone: 'success' },
  EXPIRED: { label: '만료', tone: 'outline' },
  REVOKED: { label: '회수됨', tone: 'destructive' },
};

export const UGOT_PRO_REFUND_STATUS_LABELS: Record<UgotProRefundStatus, { label: string; tone: BadgeTone }> = {
  PENDING: { label: '검토 대기', tone: 'warning' },
  APPROVED: { label: '승인됨 · 처리 전', tone: 'warning' },
  PROVIDER_PENDING: { label: 'Paddle 승인 대기', tone: 'warning' },
  PROCESSED: { label: 'Paddle 접수 · 웹훅 확정 대기', tone: 'success' },
  REJECTED: { label: '거절', tone: 'outline' },
  FAILED: { label: '처리 실패', tone: 'destructive' },
};

const IN_PROGRESS: ReadonlySet<UgotProRefundStatus> = new Set(['PENDING', 'APPROVED', 'PROVIDER_PENDING']);

export const isUgotProRefundInProgress = (status: UgotProRefundStatus): boolean => IN_PROGRESS.has(status);

/** 주문별 최신 환불 요청. */
export const latestRefundByOrder = (requests: UgotProRefundRequest[]): Map<string, UgotProRefundRequest> => {
  const latest = new Map<string, UgotProRefundRequest>();
  for (const request of requests) {
    const current = latest.get(request.orderId);
    if (!current || request.refundRequestId > current.refundRequestId) latest.set(request.orderId, request);
  }
  return latest;
};

const REFUNDABLE_ORDER_STATUSES = new Set(['PAID', 'COMPLETED']);

/** 환불 버튼: 결제된 주문이고, 진행 중이거나 이미 Paddle 이 받아들인 환불이 없을 때. */
export const canRefundUgotProOrder = (order: UgotProAdminOrder, latest: UgotProRefundRequest | undefined): boolean => {
  if (!REFUNDABLE_ORDER_STATUSES.has(order.status.toUpperCase())) return false;
  if (order.amount !== null && order.amount <= 0) return false;
  return !latest || latest.status === 'REJECTED' || latest.status === 'FAILED';
};

export type UgotProCancelOptions = { periodEnd: boolean; immediate: boolean; scheduledEnd: string | null };

/**
 * 해지 버튼 구성. ACTIVE 구독만 해지할 수 있다. 기간 말 해지가 이미 예약되어(autoRenewing=false, endDate 있음)
 * 있으면 "즉시 해지"만 남긴다.
 */
export const ugotProCancelOptions = (subscription: UgotProAdminSubscription): UgotProCancelOptions => {
  if (subscription.status.toUpperCase() !== 'ACTIVE') return { periodEnd: false, immediate: false, scheduledEnd: null };
  const scheduled = subscription.autoRenewing === false && Boolean(subscription.endDate);
  return { periodEnd: !scheduled, immediate: true, scheduledEnd: scheduled ? subscription.endDate : null };
};

const ZERO_DECIMAL_CURRENCIES = new Set(['KRW', 'JPY']);

/** 화면 입력(주 단위, 예 "12.50" 달러)을 최소 단위 정수로. 빈 값은 전액(null). KRW·JPY 는 소수 없음. */
export const parseUgotProRefundAmount = (value: string, currency: string | null): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const zeroDecimal = ZERO_DECIMAL_CURRENCIES.has((currency ?? '').toUpperCase());
  if (!(zeroDecimal ? /^\d+$/ : /^\d+(\.\d{1,2})?$/).test(trimmed)) {
    throw new Error(zeroDecimal ? '금액은 정수로 적어 주세요.' : '금액은 숫자로, 소수점 둘째 자리까지 적어 주세요.');
  }
  return zeroDecimal ? Number(trimmed) : Math.round(Number(trimmed) * 100);
};
